import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, Activity, Plus, MoreHorizontal, Calendar, Users, Settings, 
  FileText, MessageSquare, BarChart, Search, Bell, Bookmark, Star, Heart,
  ShoppingCart, Camera, Map, Briefcase, Code, ChartPie
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useMobileFooterNavSettingsWithBrand } from '@/hooks/useMobileFooterNavSettingsWithBrand';

interface MobileFooterNavProps {
  onQuickAdd: () => void;
  onToggleSidebar: () => void;
}

const iconMap = {
  Home,
  Activity,
  Plus,
  MoreHorizontal,
  Calendar,
  Users,
  Settings,
  FileText,
  MessageSquare,
  BarChart,
  Search,
  Bell,
  Bookmark,
  Star,
  Heart,
  ShoppingCart,
  Camera,
  Map,
  Briefcase,
  Code,
  ChartPie,
};

export const MobileFooterNav: React.FC<MobileFooterNavProps> = ({
  onQuickAdd,
  onToggleSidebar,
}) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useMobileFooterNavSettingsWithBrand();
  const [hideForKeyboard, setHideForKeyboard] = React.useState(false);

  // Hide nav when mobile keyboard is open
  React.useEffect(() => {
    if (!isMobile || !window.visualViewport) return;
    
    let t: number | undefined;
    const onResize = () => {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(() => {
        const vh = window.innerHeight;
        const vvh = window.visualViewport?.height || vh;
        const diff = vh - vvh;
        setHideForKeyboard(diff > 150); // treat only large diffs as keyboard
      }, 80);
    };
    
    window.visualViewport.addEventListener('resize', onResize);
    onResize();
    
    return () => {
      if (t) window.clearTimeout(t);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, [isMobile]);

  if (!isMobile || !settings?.enabled || hideForKeyboard) {
    return null;
  }

  const items = Array.isArray(settings.items) 
    ? settings.items
        .filter((item: any) => item.visible)
        .sort((a: any, b: any) => a.order - b.order)
    : [];

  const gridCols = Math.min(items.length, 5);

  const handleItemClick = (item: any) => {
    switch (item.action) {
      case 'navigate':
        if (item.path) {
          navigate(item.path);
        }
        break;
      case 'quick-add':
        onQuickAdd();
        break;
      case 'toggle-sidebar':
        onToggleSidebar();
        break;
    }
  };

  const isActive = (item: any) => {
    if (item.action === 'navigate' && item.path) {
      return location.pathname === item.path;
    }
    return false;
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 md:hidden pointer-events-none" style={{ transform: 'translateZ(0)', willChange: 'transform', backfaceVisibility: 'hidden' }}>
      <div className="bg-background/95 backdrop-blur-md border border-border shadow-2xl rounded-[2rem] overflow-hidden pointer-events-auto" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div 
          className="grid h-16 max-w-screen-sm mx-auto"
          style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}
        >
          {items.map((item: any) => {
            const Icon = iconMap[item.icon as keyof typeof iconMap] || Home;
            const active = isActive(item);
            
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                className={cn(
                  "flex flex-col items-center justify-center gap-0.5 transition-all mx-1 my-1 rounded-full relative",
                  active 
                    ? "bg-muted/80" 
                    : "hover:bg-muted/50"
                )}
              >
                <div className="relative">
                  <Icon className={cn("h-7 w-7", active ? "text-foreground" : "text-muted-foreground")} />
                  {item.showBadge && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 bg-destructive rounded-full" />
                  )}
                </div>
                <span className={cn("text-[10px] font-medium", active ? "text-foreground" : "text-muted-foreground")}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
